import React, { useState } from 'react'
import { groupBy } from 'lodash'
import { Trans, t, Plural } from '@lingui/macro'

import {
  PathwayModelAsStudent,
  WorkspaceModelAsMember
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { SearchSortEnum } from 'shared/enums/search'
import { linkBuilder } from 'shared/linkBuilder'
import CardGrid from '../../components/common/card-grid/CardGrid'
import HeadTitle from '../../components/common/head/HeadTitle'
import PageHeader from '../../components/common/page/header/PageHeader'
import { Page } from '../../components/common/page/Page'
import Select from '../../components/common/select/Select'
import WorkspaceCard from '../home-page/workspace-card/WorkspaceCard'
import EventCard, {
  EventWithAttendeesCount
} from '../manage-events/components/event-card/EventCard'
import PathwayCard from '../student-pathways-page/components/PathwayCard'
import {
  ResourceCard,
  resourceCardCoverImageSrcSet
} from '../workspace-page/resource-card/ResourceCard'
import { useSearchQuery } from './operations/searchResults.codegen'
import IconEmptySearch from 'public/assets/img/empty-search.svg'

import './SearchResults.less'
import { Box } from 'front-end/src/components'

export let latestVisitedSearchTopic: any | null = null

export const setLatestVisitedSearchTopic = (topicId: number) => {
  latestVisitedSearchTopic = topicId
}

const getSortOptions = () => {
  const sortLabels = {
    [SearchSortEnum.MOST_RELEVANT]: t`Most relevant`,
    [SearchSortEnum.NEWEST]: t`Newest`,
    [SearchSortEnum.OLDEST]: t`Oldest`,
    [SearchSortEnum.RECENTLY_UPDATED]: t`Recently updated`
  }

  return Object.values(SearchSortEnum).map((type) => {
    return {
      label: sortLabels[type],
      value: type
    }
  })
}

const scrollToTopic = (topicId: number) => {
  const element = document.querySelectorAll(
    `[href="#${linkBuilder.topicUrl(topicId)}"]`
  )[0]
  if (element) {
    element.scrollIntoView(true)
  }
}

export const SearchResultsPage = ({ match }) => {
  const [sort, setSort] = useState(SearchSortEnum.MOST_RELEVANT)
  const { data } = useSearchQuery({
    variables: {
      query: match.params.query.substring(0, 255),
      sort
    }
  })

  const results = data?.search

  if (latestVisitedSearchTopic) {
    scrollToTopic(latestVisitedSearchTopic)
    latestVisitedSearchTopic = null
  }

  const renderTopicCards = (topics) => {
    return topics.map((topic) => {
      const {
        currentTopicVersion: { title, summary, readDurationMins },
        lastEnrolment,
        author,
        coverImage,
        coverImageVariants,
        pathway_id,
        id: topic_id
      } = topic

      let image = null
      if (coverImage) {
        const resizedImage = coverImageVariants.find(
          (imageVariant) => imageVariant.size_key === '300x200'
        )
        image = resizedImage ? resizedImage.cdnUrl : coverImage.cdnUrl
      }

      return (
        <ResourceCard
          key={topic_id}
          title={title}
          description={summary}
          enrolment={lastEnrolment}
          topicId={topic_id}
          pathway={{ id: pathway_id }}
          author={{ display_name: author.display_name }}
          readDurationMins={readDurationMins}
          image={image}
          imageSrcSet={resourceCardCoverImageSrcSet(coverImageVariants)}
        />
      )
    })
  }

  const resultCount =
    results?.filter(
      ({ __typename }) => __typename !== 'CertificationModelAsStudent'
    ).length ?? 0

  const resultsByType = groupBy(results, '__typename')

  const sortOptions = getSortOptions()
  return (
    <Page className="SearchResults has-fixing-header">
      <HeadTitle
        title={t`Search results for "${match.params.query.substring(0, 255)}"`}
      />
      <PageHeader
        title={t`Search results for "${match.params.query.substring(0, 255)}"`}
        className="PageHeader"
        showBackButton={false}
      />

      {resultCount > 0 && (
        <div className="SearchResults__topbar">
          <p className="SearchResults__topbar__resultsCount">
            <Plural
              value={resultCount}
              one="# result found"
              other="# results found"
            />
          </p>

          <div className="SearchResults__topbar__sort">
            <Box ml="8px" w="210px">
              <Select
                label={<Trans>Sort by</Trans>}
                className="SearchSortSelect"
                options={sortOptions}
                value={sortOptions.find((option) => option.value === sort)}
                onChange={(option) => {
                  setSort(option?.value)
                }}
              />
            </Box>
          </div>
        </div>
      )}

      {resultsByType.hasOwnProperty('TopicModelAsMember') && (
        <>
          <h3>
            <Trans>Resources</Trans>
          </h3>
          <CardGrid
            cards={renderTopicCards(resultsByType.TopicModelAsMember)}
          />
        </>
      )}

      {resultsByType.hasOwnProperty('WorkspaceModelAsMember') && (
        <div className="SearchWorkspacesGrid">
          <h3>
            <Trans>Workspaces</Trans>
          </h3>
          <CardGrid
            cards={resultsByType.WorkspaceModelAsMember.map(
              (workspace, idx) => {
                workspace = workspace as WorkspaceModelAsMember
                let image: string | null | undefined = null

                if (workspace.coverImage) {
                  const { cdnUrl, imageVariants } = workspace.coverImage

                  const imageVariant = imageVariants.find(
                    (variant) => variant.size_key === '600x300'
                  )

                  image = imageVariant ? imageVariant.cdnUrl : cdnUrl
                }

                return (
                  <WorkspaceCard
                    key={idx}
                    id={workspace.id}
                    title={workspace.name}
                    itemsCount={workspace.publishedTopicsCount}
                    image={image}
                  />
                )
              }
            )}
          />
        </div>
      )}

      {resultsByType.hasOwnProperty('PathwayModelAsStudent') && (
        <div className="SearchPathwaysGrid">
          <h3>
            <Trans>Pathways</Trans>
          </h3>
          <CardGrid
            cards={resultsByType.PathwayModelAsStudent.map((pathway) => {
              pathway = pathway as PathwayModelAsStudent

              return (
                <PathwayCard
                  linkUrl={linkBuilder.pathwayDetail(pathway.id)}
                  key={pathway.id}
                  pathway={pathway}
                  progress={pathway.currentEnrolment?.progress}
                />
              )
            })}
          />
        </div>
      )}

      {resultsByType.hasOwnProperty('EventModelAsStudent') && (
        <div className="SearchEventsGrid">
          <h3>
            <Trans>Events</Trans>
          </h3>
          <CardGrid
            cards={resultsByType.EventModelAsStudent.map((eventUntyped) => {
              // not sure why we can't assign to the parameter here, gotta use a new variable
              const event = eventUntyped as EventWithAttendeesCount
              return (
                <a href={`${linkBuilder.eventDetail(event.id)}`} key={event.id}>
                  <EventCard event={eventUntyped} key={event.id} />
                </a>
              )
            })}
          />
        </div>
      )}

      {results && results.length === 0 && (
        <div className="no-items">
          <img src={IconEmptySearch} alt={t`Empty search`} />
          <h2>
            <Trans>No results found</Trans>
          </h2>
          <p>
            <Trans>Perhaps try altering your search?</Trans>
          </p>
        </div>
      )}
    </Page>
  )
}
